import { useId, useRef } from 'react';
import { Text } from '@hopskipdrive/react-components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { Accordion, AccordionSummary, Box, Checkbox, FormControlLabel, InputBase } from '@mui/material';

import { colorPalette } from '../variables';

import type { FilterSection } from './FilterDrawer';
import { styles } from './FilterDrawer.styles';
import { useCollapsedSectionFocus } from './useCollapsedSectionFocus';

const SECTION_TRANSITION_DURATION = 180;
const disabledTextStyle = { color: colorPalette.neutralVariant60 };

interface FilterDrawerSectionProps {
  section: FilterSection;
  selected: string[];
  searchQuery: string;
  expanded: boolean;
  animateSections: boolean;
  prefersReducedMotion: boolean;
  onToggle: () => void;
  onCheckbox: (value: string, checked: boolean) => void;
  onSearch: (query: string) => void;
}

export const FilterDrawerSection = ({
  section,
  selected,
  searchQuery,
  expanded,
  animateSections,
  prefersReducedMotion,
  onToggle,
  onCheckbox,
  onSearch,
}: FilterDrawerSectionProps) => {
  const contentId = useId();
  const headerRef = useRef<HTMLDivElement>(null);
  const isSearchActive = Boolean(section.searchable) && searchQuery.trim().length > 0;
  const isEmpty = !section.isLoading && !isSearchActive && section.options.length === 0;
  const isExpanded = !isEmpty && expanded;
  const shouldAnimate = animateSections && !prefersReducedMotion;
  const hasNoSearchResults =
    isSearchActive && !section.isLoading && !section.isSearching && section.options.length === 0;

  const contentRef = useCollapsedSectionFocus(isExpanded, headerRef);

  const chevronStyles = {
    ...styles.chevron,
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
    transition: shouldAnimate ? `transform ${SECTION_TRANSITION_DURATION}ms ease-in-out` : 'none',
  };

  const getChevron = () => {
    if (isEmpty) {
      return <ChevronRightIcon sx={styles.chevronDisabled} />;
    }
    if (animateSections) {
      return <ChevronRightIcon sx={chevronStyles} />;
    }

    return isExpanded ? <ExpandMoreIcon sx={styles.chevron} /> : <ChevronRightIcon sx={styles.chevron} />;
  };

  const content = (
    <Box
      data-testid={`filter-section-content-${section.key}`}
      ref={contentRef}
      // React 18 forwards the native inert attribute as a string. Apply it at
      // the start of exit, rather than waiting for Collapse to finish/unmount.
      {...(!isExpanded ? { inert: '' } : {})}
      aria-hidden={!isExpanded ? true : undefined}
    >
      {section.searchable && (
        <Box sx={styles.searchContainer}>
          <SearchIcon sx={styles.searchIcon} />
          <InputBase
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={section.searchPlaceholder ?? 'Search...'}
            sx={styles.searchInputBase}
            inputProps={{ 'aria-label': `Search ${section.label}` }}
          />
        </Box>
      )}
      <Box sx={styles.optionsList}>
        {hasNoSearchResults && (
          <Text style={disabledTextStyle} sx={styles.noResultsMessage}>
            No results found for &quot;{searchQuery}&quot;
          </Text>
        )}
        {section.options.map((option) => (
          <FormControlLabel
            key={option.value}
            sx={styles.checkboxFormControl}
            control={
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={(e) => onCheckbox(option.value, e.target.checked)}
                sx={styles.checkbox}
                size="small"
              />
            }
            label={option.label}
          />
        ))}
        {section.hasMore && section.onLoadMore && (
          <Box sx={styles.showMoreButton} onClick={section.onLoadMore} component="button" type="button">
            Show more
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Accordion
      expanded={isExpanded}
      // MUI 5 can emit Space activation from a focused disabled summary.
      // Keep the parent's saved expansion unchanged while options are empty.
      onChange={isEmpty ? undefined : onToggle}
      disabled={isEmpty}
      disableGutters
      elevation={0}
      square
      sx={{
        ...styles.sectionRow,
        bgcolor: 'transparent',
        '&.Mui-disabled': { bgcolor: 'transparent' },
        '&::before': { display: 'none' },
        // Keep MUI's transition subtree stable across live preference changes.
        // Override inline height/duration immediately, even during an exit.
        '& .MuiCollapse-root': shouldAnimate
          ? undefined
          : {
              transition: 'none !important',
              height: isExpanded ? 'auto !important' : '0px !important',
              overflow: 'hidden !important',
            },
      }}
      slotProps={{
        transition: {
          timeout: shouldAnimate ? SECTION_TRANSITION_DURATION : 0,
          easing: 'ease-in-out',
          mountOnEnter: true,
          unmountOnExit: true,
        },
      }}
    >
      <AccordionSummary
        ref={headerRef}
        id={`${contentId}-header`}
        aria-controls={contentId}
        data-testid={`filter-section-${section.key}`}
        disableRipple
        sx={{
          ...(isEmpty ? styles.sectionHeaderDisabled : styles.sectionHeader),
          minHeight: 0,
          '&.Mui-disabled': { opacity: 1 },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${colorPalette.navy50}`,
            outlineOffset: '-2px',
          },
        }}
      >
        <Box sx={styles.sectionHeaderLeft}>
          {getChevron()}
          <Text
            style={isEmpty ? disabledTextStyle : undefined}
            sx={isEmpty ? styles.sectionLabelDisabled : styles.sectionLabel}
          >
            {section.label}
          </Text>
        </Box>
        {isEmpty ? (
          <Text style={disabledTextStyle} sx={styles.emptyMessage}>
            {section.emptyMessage ?? `No ${section.label.toLowerCase()} available`}
          </Text>
        ) : (
          selected.length > 0 && <Box sx={styles.badge}>{selected.length}</Box>
        )}
      </AccordionSummary>
      {/* Default consumers still remove controls synchronously on close. */}
      {animateSections || isExpanded ? content : null}
    </Accordion>
  );
};
